<template>
  <AuthLayout>
    <template #form>
      <div class="forgot-password-form">
        <div v-if="!resetPasswordSubmitted" class="forgot-password-content">
          <!-- Logo and Title Section -->
          <AuthFormHeader
            variant="section"
            title="Forgot your password?"
            subtitle="We'll email you a password reset link."
          />

          <!-- Form -->
          <div class="auth-form">
            <div>
              <an-input
                label="Email address"
                type="email"
                v-model="email"
                :invalid="!emailValid"
                placeholder="name@email.com"
                :theme="theme"
                size="lg"
                focus
                @input="showButton = true"
                @keyup.enter="submit"
              />
              <div v-if="!emailValid && emailTouched" class="auth-field-error" data-cy="forgot-password-email-error">
                {{ errorMessage }}
              </div>
            </div>

            <an-button @click="submit" variant="purple" size="lg" :isWorking="isWorking" :rounded="false">
              Reset password
            </an-button>

            <an-link @click="navigateToLogin" class="auth-link-secondary"> Return to log in </an-link>
          </div>
        </div>

        <!-- Success State -->
        <div v-else class="forgot-password-content">
          <AuthFormHeader
            variant="section"
            title="Check your inbox"
            subtitle="We've sent a reset link to your inbox."
          />

          <div class="auth-form">
            <an-button @click="navigateToLogin" variant="purple" size="lg" :rounded="false"> Back to log in </an-button>

            <an-link @click="submit" class="auth-link-secondary"> Resend email </an-link>
          </div>
        </div>
      </div>
    </template>
  </AuthLayout>
</template>

<script>
import AuthLayout from '@/components/Auth/AuthLayout';
import AuthFormHeader from '@/components/Auth/AuthFormHeader';
import AuthBrandMixin from '@/mixins/AuthBrandMixin';
import { isValidEmail, normalizeEmail } from '@/utils/email';
import api from '@/api';
import { getRecaptchaToken } from '@/utils/recaptcha';

export default {
  data() {
    return {
      email: '',
      emailValid: true,
      emailTouched: false,
      errorMessage: '',
      showButton: true,
      isWorking: false,
      resetPasswordSubmitted: false
    };
  },
  mixins: [AuthBrandMixin],
  components: {
    AuthLayout,
    AuthFormHeader
  },
  mounted() {
    if (this.$route.query.email) {
      this.email = this.$route.query.email;
      this.showButton = true;
    }
    this.applyAuthTheme();
  },
  methods: {
    async submit() {
      try {
        this.isWorking = true;
        const email = normalizeEmail(this.email);
        this.emailValid = isValidEmail(email);
        this.emailTouched = true;

        this.$trackEvent('forgot-password.reset-password-button.click');

        if (this.emailValid) {
          this.errorMessage = '';
          // Get reCAPTCHA token
          const captchaToken = await getRecaptchaToken('forgot_password');

          await api.post('/forgot_password', {
            email,
            captcha_token: captchaToken
          });
          this.$trackEvent('forgot-password.reset-password.success');
          this.resetPasswordSubmitted = true;
        } else {
          this.errorMessage = 'Please enter a valid email address';
        }
      } catch (err) {
        const { response } = err;
        const message = response?.data?.message || 'Something went wrong! Please try again with a valid email';
        this.errorMessage = message;
        this.emailValid = false;
        this.emailTouched = true;
        this.$trackEvent('forgot-password.reset-password.failure');
      } finally {
        this.isWorking = false;
      }
    },
    navigateToLogin() {
      this.$trackEvent('forgot-password.navigate-to-login.click');
      this.$router.replace({ name: 'login', query: this.$route.query });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/_mixins.scss';
@import '@/styles/_auth.scss';
@import '@/styles/_auth-tokens.scss';

.forgot-password-form {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.forgot-password-content {
  display: flex;
  flex-direction: column;
  gap: $auth-gap-lg;
  padding: 0 $auth-padding-horizontal;
}

.auth-field-error {
  margin-top: 8px;
  text-align: left;
  font-size: 14px;
  color: var(--red);
}

// All shared styles moved to _auth.scss
</style>
